import api from '@/services/api/api';
import axios from 'axios';
import store from '@/store'
import { $dayjs } from "@/plugins/dayjs"

const initialState = () => ({
  fetching: null,
  error: null,
  calendars: [],
  success: null,
  userPendingCalendars: [],
  teacherSurveys: [],
  meetings: [],
  recordings: [],
  attendance: []
});

export default {
  namespaced: true,
  state: initialState(),
  mutations: {
    RESET_STATE(state){
      state.fetching = null
      state.error = null
      state.calendars = []
      state.success = null
      state.teacherSurveys = []
      state.meetings = []
      state.recordings = []
      state.attendance = []
    },
    FETCHING(state, value){
      state.fetching = !!value;
    },
    FETCH_SUCCESS(state, value){
      state.success = !!value;
    },
    FETCH_ERROR(state, value){
      state.error = value;
    },
    SET_CALENDAR_LIST(state, values){
      state.calendars = values;
    },
    SET_USER_CALENDAR_LIST(state, values){
      state.userPendingCalendars = []

      values.forEach(calendar => {
        if(calendar.pendiente){
          state.userPendingCalendars.push(calendar.curso_id)
        }
      })
    },
    SET_MEETINGS(state, payload) {
      state.meetings = payload
    },
    SET_RECORDINGS(state, payload) {
      state.recordings = payload
    },
    SET_ATTENDANCE(state, payload) {
      state.attendance = payload
    },
    UPDATE_ATTENDANCE(state, classNumber) {
      state.attendance.contactos_presentes.push({
        clase: classNumber,
      })
    },
    UPSERT_RECORDING_RATE(state, args) {
      const npsList = state.attendance.nps_clases;

      const index = npsList.findIndex(item =>
        Number(item.clase) === Number(args.classNumber)
      );

      if (index !== -1) {
        const existing = npsList[index];

        npsList.splice(index, 1, {
          ...existing,
          valoracion: args.rate,
          comentario: args.comment
        });
      } else {
        npsList.push({
          calendario_id: args.calendarId,
          contacto_id: args.userId,
          clase: args.classNumber,
          valoracion: args.rate,
          comentario: args.comment
        });
      }
    }
  },
  actions: {
    async fetch({ commit, rootState }, args){
      commit('RESET_STATE');
      commit('FETCHING', true);

      const params = {
        pais_id: rootState.Init.country.pais_id,
        sin_completos: 1,
        cursos_ids: args.course
      }

      if(args.start){
        params['fecha_desde'] = args.start
      }

      return await axios
        .get(process.env.VUE_APP_EDUCACIONIT_API_SERVER + "public/calendarios", {params})
        .then(response => {
          commit('FETCH_SUCCESS', true);
          commit('FETCHING', false);
          commit('SET_CALENDAR_LIST', response.data);
          return response;
        })
        .catch(error => {
          commit('FETCH_SUCCESS', false);
          commit('FETCHING', false);

          if(404 === error.response.status){
            commit('SET_CALENDAR_LIST', []);
            return error.response;
          }

          commit('FETCH_ERROR', error);

          return error.response;
        });
    },
    async reset({ commit }){
      commit('RESET_STATE');
    },
    async getForUser({ commit }) {
      try{
        const response = await api.get('/plugin/calendar/user');
        commit('SET_USER_CALENDAR_LIST', response.data.data);
        return response.data.data;
      }catch (e) {
        commit('SET_USER_CALENDAR_LIST', []);
        return [];
      }
    },
    async getTeacherSurveys({ commit, state }, args) {
      const params = {
        calendar_id: args.calendar_id
      }

      try{
        const response = await api.get('/plugin/calendar/surveys', {params});
        state.teacherSurveys = response.data
        return response.data;
      }catch (e) {
        return [];
      }
    },
    async getRecordings({ commit, state }, args) {
      const params = {
        calendar_id: args.calendar.calendario_id
      }

      const datePerClass = new Map(
        args.calendar.clases.map(c => [Number(c.clase), c.fecha_inicio])
      );

      try{
        const recordings = await api.get('/plugin/calendar/recordings', {params});

        recordings.data.forEach(recording => {
          recording.date = datePerClass.get(Number(recording.class_number))
        })

        const recordingsMap = recordings.data.reduce((map, { id, class_number, date, process_date }) => {
          if (!map.has(class_number)) map.set(class_number, { date, parts: [] })
          const entry = map.get(class_number)
          entry.parts.push({ id, process_date })
          return map
        }, new Map())

        const mapped = Array.from(recordingsMap, ([class_number, {date, parts}]) => ({
          class_number,
          date,
          parts: parts
            .slice()
            .sort((a, b) => $dayjs(a.process_date).valueOf() - $dayjs(b.process_date).valueOf())
        }))

        commit('SET_RECORDINGS', mapped)
        return mapped;
      }catch (e) {
        commit('SET_RECORDINGS', [])
        return [];
      }
    },
    async getMeetings({ commit, state }, args) {
      const params = {
        calendar_id: args.calendar_id
      }

      try{
        const meetings = await api.get('/plugin/calendar/meetings', {params});

        commit('SET_MEETINGS', meetings.data)
        return meetings.data;
      }catch (e) {
        commit('SET_MEETINGS', [])
        return [];
      }
    },
    async getAttendance({ commit, state }, args) {
      const params = {
        calendar_id: args.calendar_id
      }

      try{
        const response = await api.get('/plugin/contact/calendar', {params});

        commit('SET_ATTENDANCE', response.data.data[0])
        return response.data.data[0];
      }catch (e) {
        commit('SET_ATTENDANCE', [])
        return [];
      }
    },
    async markAttendance({ commit, state }, args) {
      const options = {
        headers:{
          'Authorization': `Bearer ${await store.state.AuthFirebase.auth.currentUser?.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      };

      const params = {
        clase: args.classNumber,
        presente_tipo_id: 'R'
      }

      try {
        const response = await axios.post(
          process.env.VUE_APP_EDUCACIONIT_API_SERVER + `contactos/${args.userId}/calendarios/${args.calendarId}/presentes`,
          params,
          options
        );

        commit('UPDATE_ATTENDANCE', args.classNumber)

        return response.data
      } catch (err) {
        console.error(err);
      }
    },
    async rateRecording({ commit, state }, args) {
      const options = {
        headers:{
          'Authorization': `Bearer ${await store.state.AuthFirebase.auth.currentUser?.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      };

      const params = {
        valoracion: args.rate
      }

      if (args.comment && args.comment.trim() !== '') {
        params.comentario = args.comment;
      }

      const response = await axios.post(
        process.env.VUE_APP_EDUCACIONIT_API_SERVER + `contactos/${args.userId}/calendarios/${args.calendarId}/clases/${args.classNumber}/nps`,
        params,
        options
      );

      /*const options = {
        headers:{
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      };

      let params = new URLSearchParams();

      params.append('calendar_id', args.calendarId);
      params.append('class_number', args.classNumber);
      params.append('rate', args.rate);

      if(args.comment !== null && args.comment !== '') {
        params.append('comment', args.comment);
      }

      const response = await api.post('/plugin/calendar/recording/rate', params, options);*/
      commit('UPSERT_RECORDING_RATE', args)

      return response.data;
    }
  },
  getters: {
    fetching: (state) => state.fetching,
    error: (state) => state.error,
    calendars: (state) => state.calendars,
    success: (state) => state.success,
    userPendingCalendars: (state) => state.userPendingCalendars,
    teacherSurveys: (state) => state.teacherSurveys,
    meetings: (state) => state.meetings,
    recordings: (state) => state.recordings,
    attendance: (state) => state.attendance
  },
};
